import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Bell, Check, X, Eye, Clock } from "lucide-react";
import { toast } from "sonner";

const PRIORITY_STYLE = {
  CRITICAL: "pnl-down border-current",
  HIGH: "text-signal-yellow border-current",
  MEDIUM: "text-signal-blue border-current",
  LOW: "text-muted-foreground border-current",
};

export default function InboxDropdown() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const nav = useNavigate();

  const load = async () => {
    try {
      const [i, u] = await Promise.all([
        api.get("/inbox", { params: { limit: 30 } }),
        api.get("/inbox/unread-count"),
      ]);
      setItems(i.data); setUnread(u.data.count);
    } catch { /* noop */ }
  };
  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, []);

  const act = async (n, action) => {
    try {
      await api.post(`/inbox/${n.id}/action`, { action });
      toast.success(`Marked ${action}`);
      load();
    } catch { toast.error("Failed"); }
  };
  const openTarget = async (n) => {
    setOpen(false);
    // Mark read (fire-and-forget) — never blocks navigation
    act(n, "read");

    const symbol = (n.subject || "").split("·")[1]?.trim();

    // For symbol-scoped notifications, open the drawer (Chart tab) — no AI call
    if (n.target_kind === "opportunity" || n.target_kind === "analysis") {
      if (symbol) {
        window.dispatchEvent(new CustomEvent("tm:open-drawer",
          { detail: { symbol, tab: "chart" } }));
        return;
      }
      // Fallback: try direct proposal id
      if (n.target_id) { nav(`/proposal/${n.target_id}`); return; }
    }
    if (n.target_kind === "proposal") {
      if (n.target_id) { nav(`/proposal/${n.target_id}`); return; }
      toast.error("Proposal target missing on this notification");
      return;
    }
    if (n.target_kind === "position" || n.target_kind === "position_review") {
      nav(n.target_id ? `/positions?highlight=${n.target_id}` : "/positions");
      return;
    }
    if (n.target_kind === "risk_warning") {
      nav("/risk");
      return;
    }
    if (n.target_kind === "inbox_recommendation") {
      // Just keep the popover open on the same item — nothing else to open
      toast.info("This is a summary recommendation.");
      return;
    }
    // Unknown target — surface it instead of swallowing
    toast.error(`Cannot open target of type '${n.target_kind || "unknown"}'`);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className="btn-ghost !px-2 relative" data-testid="inbox-bell">
          <Bell size={16} strokeWidth={1.5}/>
          {unread > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-4 h-4 px-1 bg-signal-red text-white text-[10px] mono flex items-center justify-center rounded-full">{unread}</span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 !rounded-none !p-0 border-border/70 max-h-[70vh] overflow-auto">
        <div className="px-3 h-9 border-b border-border/60 flex items-center">
          <div className="label-cap">AI Inbox</div>
          <div className="ml-auto text-[10px] mono text-muted-foreground uppercase tracking-widest">{items.length} items</div>
        </div>
        {items.length === 0 && <div className="p-6 text-center text-xs text-muted-foreground">Nothing here yet. Run a scan on the Opportunities page.</div>}
        <div className="divide-y divide-border/40">
          {items.map(n => (
            <div key={n.id} className={`px-3 py-2.5 ${n.status === "unread" ? "bg-secondary/25" : ""}`} data-testid={`inbox-item-${n.id}`}>
              <div className="flex items-center gap-2">
                <span className={`chip !text-[9px] ${PRIORITY_STYLE[n.priority]}`}>{n.priority}</span>
                <span className="text-[10px] mono uppercase text-muted-foreground tracking-widest">{n.kind.replace(/_/g, " ")}</span>
                <span className="ml-auto text-[10px] mono text-muted-foreground">{new Date(n.created_at).toLocaleTimeString()}</span>
              </div>
              <div className="text-sm mt-1 line-clamp-1">{n.subject}</div>
              {n.summary && <div className="text-[11px] text-muted-foreground mt-0.5 line-clamp-2">{n.summary}</div>}
              <div className="flex items-center gap-1 mt-2">
                <button className="btn-outline h-6 !px-2 text-[10px]" onClick={()=>openTarget(n)}><Eye size={10}/> Review</button>
                <button className="btn-outline h-6 !px-2 text-[10px]" onClick={()=>act(n, "approved")}><Check size={10}/> Approve</button>
                <button className="btn-ghost h-6 !px-2 text-[10px]" onClick={()=>act(n, "snoozed")}><Clock size={10}/> Snooze</button>
                <button className="btn-ghost h-6 !px-2 text-[10px]" onClick={()=>act(n, "dismissed")}><X size={10}/> Dismiss</button>
              </div>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
